// Relatório de acertos entre datas: escolhe o período e o colaborador (ou
// todos), confere na tela o que vai sair e baixa o PDF com o resumo e o
// demonstrativo de cada acerto. Serve para auditoria, fiscalização, Justiça…
// Funciona sem sinal (usa o que já está no aparelho).
import { acertos, categorias, colaboradores, tudoDosAcertos, veiculos } from '../dados.js';
import { itensDoAcerto } from '../lib/acerto.js';
import { h, trocar } from '../lib/dom.js';
import { mensagemDeErro } from '../lib/erros.js';
import { data, dinheiro, plural } from '../lib/formato.js';
import { entregarArquivo } from '../lib/recibo.js';
import { nomeDoArquivoDoRelatorio, periodoCurto, relatorioDeAcertos, relatorioEmPdf, situacaoNoRelatorio } from '../lib/relatorio.js';
import { hojeDaEmpresa, sessao } from '../sessao.js';
import { casca } from '../ui/casca.js';
import { avisar, campo, carregando, faixa, secao } from '../ui/componentes.js';
import { nomeCurto } from './viagens.js';

export function telaRelatorioAcertos({ busca }) {
  const corpo = h('div', { class: 'secao' }, carregando());

  (async () => {
    const [todos, pessoas] = await Promise.all([acertos.todos(), colaboradores.listar()]);
    const hoje = hojeDaEmpresa();
    const pedidaDe = busca.get('de');
    const pedidaAte = busca.get('ate');
    const dataValida = (d) => /^\d{4}-\d{2}-\d{2}$/.test(d ?? '');
    let filtro = {
      de: dataValida(pedidaDe) ? pedidaDe : `${hoje.slice(0, 4)}-01-01`,
      ate: dataValida(pedidaAte) ? pedidaAte : hoje,
      colaboradorId: busca.get('colaborador') || '',
      completo: true,
    };
    const resultado = h('div', { class: 'secao', 'aria-live': 'polite' });
    const botao = h('button', { class: 'botao cheio', type: 'button', onClick: () => gerar() }, 'Baixar o relatório em PDF');
    const erro = h('p', { class: 'erro-campo', role: 'alert' });

    const comPessoas = pessoas
      .filter((p) => todos.some((a) => a.colaborador_id === p.id))
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));

    const form = h('form', { novalidate: true, onSubmit: (e) => e.preventDefault() },
      h('div', { class: 'grade-2' },
        campo({ rotulo: 'De', nome: 'de', tipo: 'date', valor: filtro.de, atributos: { max: hoje }, aoMudar: () => mudou() }),
        campo({ rotulo: 'Até', nome: 'ate', tipo: 'date', valor: filtro.ate, atributos: { max: hoje }, aoMudar: () => mudou() })),
      campo({
        rotulo: 'Colaborador',
        nome: 'colaborador',
        tipo: 'select',
        valor: filtro.colaboradorId,
        opcoes: [['', 'Todos'], ...comPessoas.map((p) => [p.id, `${p.apelido ? `${p.nome} (${p.apelido})` : p.nome}${p.ativo ? '' : ' · desativado'}`])],
        aoMudar: () => mudou(),
      }),
      campo({
        rotulo: 'O que sai no PDF',
        nome: 'completo',
        tipo: 'segmentos',
        valor: 'sim',
        opcoes: [['sim', 'Resumo e demonstrativos', 'cada acerto completo'], ['nao', 'Só o resumo', 'uma linha por acerto']],
        aoMudar: () => mudou(),
      }),
      erro);

    function lerFiltro() {
      const valor = (nome) => form.querySelector(`[name="${nome}"]`)?.value ?? '';
      return {
        de: valor('de'),
        ate: valor('ate'),
        colaboradorId: valor('colaborador'),
        completo: form.querySelector('[name="completo"]:checked')?.value !== 'nao',
      };
    }

    function mudou() {
      filtro = lerFiltro();
      desenhar();
    }

    function problema(f) {
      if (!dataValida(f.de) || !dataValida(f.ate)) return 'Informe as duas datas.';
      if (f.de > f.ate) return 'A data inicial precisa ser antes da final.';
      return null;
    }

    function desenhar() {
      const p = problema(filtro);
      erro.textContent = p ?? '';
      botao.disabled = Boolean(p);
      if (p) {
        trocar(resultado);
        return;
      }
      const r = relatorioDeAcertos({ ...filtro, colaboradorId: filtro.colaboradorId || null, acertos: todos, colaboradores: pessoas });
      botao.disabled = r.quantidade === 0 && r.desfeitos.length === 0;
      trocar(resultado,
        r.quantidade === 0
          ? faixa(`Nenhum acerto fechado entre ${data(filtro.de)} e ${data(filtro.ate)}.`, 'info')
          : h('p', null, `${plural(r.quantidade, 'acerto')} entre ${data(filtro.de)} e ${data(filtro.ate)} (pela data do acerto).`),
        r.grupos.map((g) => secao(nomeCurto(g.colaborador), h('div', { class: 'lista' },
          g.acertos.map((a) => h('div', { class: 'linha-valor' },
            h('span', null, `Nº ${a.numero}/${a.ano} · ${periodoCurto(a)}`, h('br'), h('small', null, situacaoNoRelatorio(a))),
            h('span', null, dinheiro(a.saldo)))),
          h('dl', { class: 'linha-dados resumo' },
            h('dt', null, 'Créditos'), h('dd', null, dinheiro(g.totais.creditos)),
            h('dt', null, 'Vales'), h('dd', null, dinheiro(g.totais.vales)),
            h('dt', null, 'Despesas pagas por ele'), h('dd', null, dinheiro(g.totais.despesas)),
            h('dt', null, 'Descontos'), h('dd', null, dinheiro(g.totais.descontos)),
            g.totais.pago > 0 && [h('dt', null, 'Pago pela empresa'), h('dd', null, dinheiro(g.totais.pago))],
            g.totais.devolvido > 0 && [h('dt', null, 'Devolvido à empresa'), h('dd', null, dinheiro(g.totais.devolvido))],
            g.totais.emAberto !== 0 && [h('dt', null, 'Ainda em aberto'), h('dd', null, dinheiro(g.totais.emAberto))])),
        { contador: String(g.acertos.length) })),
        r.desfeitos.length > 0 && h('p', { class: 'pequeno' },
          `${plural(r.desfeitos.length, 'acerto desfeito', 'acertos desfeitos')} no período: ${r.desfeitos.map(({ acerto: a }) => `nº ${a.numero}/${a.ano}`).join(', ')}. `
          + 'Eles aparecem no fim do resumo, com o motivo, e não entram nos totais.'));
    }

    async function gerar() {
      filtro = lerFiltro();
      if (problema(filtro)) {
        desenhar();
        return;
      }
      botao.disabled = true;
      const antes = botao.textContent;
      botao.textContent = 'Gerando o PDF…';
      try {
        const [tudo, frota, cats] = await Promise.all([tudoDosAcertos(), veiculos.listar(), categorias.todas()]);
        const r = relatorioDeAcertos({ ...filtro, colaboradorId: filtro.colaboradorId || null, acertos: todos, colaboradores: pessoas });
        const blob = await relatorioEmPdf({
          relatorio: r,
          empresa: sessao.empresa,
          completo: filtro.completo,
          dadosDoAcerto: (a) => {
            const itens = itensDoAcerto(a.id, tudo);
            return {
              viagens: itens.viagens,
              veiculos: frota,
              lancamentos: itens.lancamentos,
              categorias: cats,
              pagamento: itens.lancamentos.find((l) => l.tipo === 'pagamento' || l.tipo === 'devolucao') ?? null,
            };
          },
        });
        const resposta = await entregarArquivo(blob, nomeDoArquivoDoRelatorio(r), 'Relatório de acertos');
        if (resposta === 'baixado') avisar('Relatório baixado em PDF.');
      } catch (e) {
        console.error(e);
        avisar(mensagemDeErro(e) || 'Não foi possível gerar o PDF agora. Tente de novo.', 'erro');
      } finally {
        if (botao.isConnected) {
          botao.disabled = false;
          botao.textContent = antes;
        }
      }
    }

    trocar(corpo,
      h('p', { class: 'pequeno' }, 'Todos os acertos fechados no período, separados por colaborador, com os totais e o demonstrativo de cada um. '
        + 'Serve para apresentar numa auditoria, fiscalização ou ação trabalhista.'),
      form,
      h('div', { class: 'acoes' }, botao),
      resultado);
    desenhar();
  })().catch((e) => {
    console.error(e);
    trocar(corpo, faixa(mensagemDeErro(e), 'perigo'));
  });

  return casca({ titulo: 'Relatório de acertos', voltar: '/acertos', conteudo: corpo });
}
